import {
    DocumentNode,
    OperationVariables,
    useApolloClient
} from '@zoopla/apollo';
import debounce from 'lodash/debounce';
import { useCallback, useMemo } from 'react';

type DebouncedFetchOptions = {
    debounceTime?: number;
    abortController?: AbortController | undefined;
};

// this the the type used by the lib so unfortunate type casting required
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ApolloData = any;

export const useDebouncedFetch = ({
    debounceTime = 200,
    abortController
}: DebouncedFetchOptions) => {
    const apolloClient = useApolloClient();

    const fetchFunction = useCallback(
        async (
            query: DocumentNode,
            variables: OperationVariables,
            resolve: (value: ApolloData | PromiseLike<ApolloData>) => void,
            reject: (reason: unknown) => void
        ) => {
            if (!variables) {
                resolve([]);
                return;
            }

            try {
                const { data } = await apolloClient.query({
                    query,
                    variables,
                    context: {
                        fetchOptions: {
                            signal: abortController?.signal
                        }
                    }
                });

                resolve(data);
            } catch (e) {
                if (abortController?.signal.aborted === true) return;
                reject(e);
            }
        },
        [apolloClient, abortController]
    );

    const debouncedFetch = useMemo(
        () => debounce(fetchFunction, debounceTime, { trailing: true }),
        [debounceTime, fetchFunction]
    );

    return <
        T = ApolloData,
        TVariables extends OperationVariables = OperationVariables
    >(
        query: DocumentNode,
        variables: TVariables
    ) =>
        new Promise<T>((resolve, reject) => {
            debouncedFetch(query, variables, resolve, reject);
        });
};
